import { AnimatePresence, motion } from 'framer-motion'
import { Button } from 'components/Button'
import { CreativeType, type Creative } from '../types'
import type { ContentShape, LivePlatform } from '../store/idea-store'
import {
  GeneratingStatusText,
  MediaStatusCenter,
  MediaStatusPill,
  toGeneratingShape,
} from './GeneratingStatusText'
import { isFailedSuggestion, xPostHasText } from '../utils/suggestion-status'
import { EditsApplyingBadge } from './EditsApplyingBadge'
import { FacebookPostPreview } from '../previews/FacebookPostPreview'
import { InstagramPostPreview } from '../previews/InstagramPostPreview'
import { LinkedInPostPreview } from '../previews/LinkedInPostPreview'
import { PinterestPinPreview } from '../previews/PinterestPinPreview'
import { TikTokVideoPreview } from '../previews/TikTokVideoPreview'
import { XPostPreview } from '../previews/XPostPreview'
import { YouTubeShortsPreview } from '../previews/YouTubeShortsPreview'
import { DARK_PREVIEW_OVERRIDES } from '../previews/preview-utils'
import { postKeyForCreative } from '../utils/post-key'
import { PostModalPreviewSkeleton } from './PostModalPreviewSkeleton'

interface PostModalPreviewPaneProps {
  /** Currently-previewed platform. Drives both the slide key and the routed preview component. */
  viewing: LivePlatform
  /** The creative for this platform, or null while the sibling is still spawning. */
  creative: Creative | null
  /** Direction of the last tab change (1 = next-right, -1 = previous-left). Drives slide easing. */
  direction: 1 | -1
  /** When provided, renders an "Edit this post" CTA pinned to the bottom-center of the preview frame. */
  onEdit?: (platform: LivePlatform) => void
  /** Arrival-view affordance: regenerate a fresh take of the draft in place. */
  onNewTake?: () => void
  /** Post shape — drives the generating narration copy while no asset exists yet. */
  shape?: ContentShape
}

const SPRING = { type: 'spring' as const, stiffness: 300, damping: 30 }

// Modal-only overlay: max width + neutralise SectionHoverWrapper "Ask AI /
// Edit" affordances so the embed reads as a read-only preview. The actual
// dark-skin recolouring lives in DARK_PREVIEW_OVERRIDES (preview-utils),
// shared with ComposerShell where section interactivity stays live.
const POST_MODAL_PREVIEW_OVERRIDES = [
  'w-full max-w-[420px]',
  '[&_.group\\/section]:!pointer-events-none',
  DARK_PREVIEW_OVERRIDES,
].join(' ')

/**
 * Right-half of PostModal. Routes the right per-platform preview component
 * given the active platform + creative, and animates a horizontal slide
 * between platform tabs (TransitionedStepper idiom: spring 300/30, direction
 * follows the user's tab travel).
 *
 * If the sibling for the active platform hasn't landed yet, renders a
 * platform-tinted skeleton so the canvas height doesn't jump.
 */
export function PostModalPreviewPane({
  viewing,
  creative,
  direction,
  onEdit,
  onNewTake,
  shape,
}: PostModalPreviewPaneProps) {
  return (
    <div className="relative h-full w-full overflow-hidden rounded-[12px] bg-bg-3">
      <AnimatePresence initial={false} mode="wait" custom={direction}>
        <motion.div
          key={viewing}
          custom={direction}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={SPRING}
          className="absolute inset-0 flex items-start justify-center overflow-y-auto px-4 pt-20 pb-4"
        >
          {/* Two layers of CSS surgery on the embedded preview:
              1. `[&_.group\/section]:!pointer-events-none` neutralises the
                 SectionHoverWrapper "Ask AI / Edit" overlays baked into the
                 social preview components — keeps this modal read-only.
              2. The light-mode chrome (white card + gray-* text) is rewritten
                 to T3 tokens so the embed feels native to the dark modal.
                 Brand-coloured links (IG blue, FB/LI blues, etc.) are not
                 grays so they're left alone. */}
          <div className={POST_MODAL_PREVIEW_OVERRIDES}>
            {creative && hasAsset(creative) ? (
              <PreviewForPlatform creative={creative} viewing={viewing} />
            ) : (
              <div className="relative">
                <PostModalPreviewSkeleton platform={viewing} />
                {/* Live narration while the post generates: SocialKit pipeline
                    beats when they stream (keyed by creativeId), rotating copy
                    otherwise. Matters most on the /post/:id arrival view, where
                    the user watches their creation intent being made. A post
                    whose generation terminally FAILED gets an honest failed
                    pill instead of fake progress (retry lives on the cards). */}
                {creative &&
                  (isFailedSuggestion(creative) ? (
                    <MediaStatusCenter>
                      <MediaStatusPill>
                        This one didn't come out. Your credit's back. Head to Posts to retry it.
                      </MediaStatusPill>
                    </MediaStatusCenter>
                  ) : (
                    <GeneratingStatusText
                      shape={shape ? toGeneratingShape(shape) : 'post'}
                      isActive
                      creativeId={creative.id}
                    />
                  ))}
              </div>
            )}
          </div>
        </motion.div>
      </AnimatePresence>

      {/* The embedded preview shows the last-RENDERED master, which lags the
          user's composition edits until the server re-renders it. While that
          render is pending, say so — otherwise the modal looks like the edits
          silently reverted. Pinned to the pane (not the sliding preview) so it
          stays put across platform-tab slides. */}
      {creative && hasAsset(creative) && creative.content?.compositionPendingRender && (
        <EditsApplyingBadge overlay className="bottom-3 left-3" creativeId={creative.id} />
      )}

      {/* Floating glass Edit CTA — lives in the same container as the
          scrolling preview so content passes cleanly behind it. We override
          the tertiary's solid surface with a paper-tinted frosted glass
          (paper/10 + backdrop-blur-xl) so the button stays readable over
          any preview imagery. Tertiary recipe keeps Schedule as the only
          acid CTA in the modal. */}
      {(onEdit || onNewTake) && creative && (
        <div className="absolute top-4 left-1/2 z-10 flex -translate-x-1/2 gap-2">
          {onEdit && (
            <Button
              variant="t3-tertiary"
              size="t3-md"
              className="!border-paper/15 !bg-paper/10 shadow-lg shadow-black/30 backdrop-blur-xl hover:!bg-paper/15"
              onClick={() => onEdit(viewing)}
            >
              Edit this post
            </Button>
          )}
          {onNewTake && (
            <Button
              variant="t3-tertiary"
              size="t3-md"
              className="!border-paper/15 !bg-paper/10 shadow-lg shadow-black/30 backdrop-blur-xl hover:!bg-paper/15"
              onClick={onNewTake}
            >
              New take
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

const slideVariants = {
  enter: (direction: 1 | -1) => ({ x: direction > 0 ? 40 : -40, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (direction: 1 | -1) => ({ x: direction > 0 ? -40 : 40, opacity: 0 }),
}

// ─── Routing ───────────────────────────────────────────────────────────────

function PreviewForPlatform({ creative, viewing }: { creative: Creative; viewing: LivePlatform }) {
  const generatingFields: Set<string> = new Set()
  // Mute is shared per post across platform tabs (see PreviewVideo).
  const postKey = postKeyForCreative(creative)
  switch (creative.type) {
    case CreativeType.InstagramPost:
      return (
        <InstagramPostPreview
          content={creative.content as never}
          generatingFields={generatingFields}
          creativeId={creative.id}
          postKey={postKey}
          compact
        />
      )
    case CreativeType.FacebookPost:
      return (
        <FacebookPostPreview
          content={creative.content as never}
          generatingFields={generatingFields}
          creativeId={creative.id}
        />
      )
    case CreativeType.LinkedInPost:
      return (
        <LinkedInPostPreview
          content={creative.content as never}
          generatingFields={generatingFields}
          creativeId={creative.id}
        />
      )
    case CreativeType.TikTokVideo:
      return (
        <TikTokVideoPreview
          content={creative.content as never}
          generatingFields={generatingFields}
          postKey={postKey}
        />
      )
    case CreativeType.YouTubeShort:
      return (
        <YouTubeShortsPreview
          content={creative.content as never}
          generatingFields={generatingFields}
          postKey={postKey}
        />
      )
    case CreativeType.PinterestPin:
      return (
        <PinterestPinPreview
          content={creative.content as never}
          generatingFields={generatingFields}
          creativeId={creative.id}
        />
      )
    // Missing until 2026-09-06: an X post fell through to the skeleton below
    // and stayed there forever (the "X preview hangs on an infinite skeleton"
    // support report), while LinkedIn rendered fine in the same modal.
    case CreativeType.XPost:
      return (
        <XPostPreview
          content={creative.content as never}
          generatingFields={generatingFields}
          creativeId={creative.id}
        />
      )
    default:
      return <PostModalPreviewSkeleton platform={viewing} />
  }
}

// ─── hasAsset ──────────────────────────────────────────────────────────────

/**
 * True once the per-platform creative has a usable visual asset (image,
 * video, or carousel slides). Drives the swap from skeleton to the real
 * preview — copy fields keep their own per-section shimmers within the
 * preview component as the agent writes them in.
 *
 * X is text-first: a tweet with no image is a finished post, so its text
 * counts as the asset. Without this a text-only X post never left the
 * skeleton either.
 */
function hasAsset(creative: Creative): boolean {
  const content = creative.content as unknown as Record<string, unknown>
  if (!content) return false
  if (content.imageUrl || content.videoUrl) return true
  if (xPostHasText(creative)) return true
  const items = content.mediaItems as unknown[] | undefined
  return Array.isArray(items) && items.length > 0
}
